/**
 * palettes-page.js — SILKhee palette accordion  ·  THE LOOM
 *
 * LAYERS (bottom → top, once expanded):
 *   z:1  Photo — blur(14px) throughout stitching, clears on finish
 *   z:2  Gradient canvas — full opacity throughout stitching, fades at end
 *   z:3  Stitch canvas — photo-colored stitches accumulate here
 *   z:4  Cursor canvas — single 1×14px white cursor, cleared each frame
 *
 * SEQUENCE PER PALETTE COLOR:
 *   Cursor starts top-left. Sweeps L→R row 0, R→L row 1, L→R row 2 ...
 *   At each 8px step: if photo pixel matches current palette color (HSL) → drawStitch
 *   One complete top→bottom pass per color, then cursor jumps back to top-left.
 *   Repeat for every palette color.
 *   After all colors: 400ms pause → gradient fades 700ms, blur clears 1000ms.
 */
(function () {
'use strict';

// ─── Constants ──────────────────────────────────────────────────────────────
const STRIP_H       = 80;
const EXPAND_H      = 360;
const SAMPLE_GRID   = 8;     // px — stitch spacing and row height
const ROW_CROSS_MS  = 20;    // ms for cursor to cross full card width
const HOLD_MS       = 120;   // ms pause after expand before reveal
const PAUSE_MS      = 60;    // ms between passes
const LOOM_PASSES   = 8;     // luminance bands for Tidal multi-image cycle
const ORCHARD_PASSES = 20;   // luminance bands for Orchard single-image (with reverse)

// ─── Module state ────────────────────────────────────────────────────────────
let activeRow  = null;
let activeAnim = null;

// ─── Exclusive codes (available in packs only, not sold separately) ──────────
const EXCLUSIVE_CODES = { '5590': true, '5594': true };

// ─── Utilities ───────────────────────────────────────────────────────────────
function hexRgb(h) {
  h = h.replace('#', '');
  if (h.length === 3) h = h[0]+h[0]+h[1]+h[1]+h[2]+h[2];
  const n = parseInt(h, 16);
  return [(n>>16)&255, (n>>8)&255, n&255];
}
function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

// ─── LAB color space + deltaE ────────────────────────────────────────────────
// Converts sRGB [0–255] → CIELAB. deltaE = Euclidean distance in LAB,
// which closely matches human perceptual color difference.
function rgbToLab(r, g, b) {
  // 1. Linearise sRGB
  function lin(c) {
    c /= 255;
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  }
  const rl = lin(r), gl = lin(g), bl = lin(b);
  // 2. sRGB → XYZ (D65)
  const X = rl * 0.4124564 + gl * 0.3575761 + bl * 0.1804375;
  const Y = rl * 0.2126729 + gl * 0.7151522 + bl * 0.0721750;
  const Z = rl * 0.0193339 + gl * 0.1191920 + bl * 0.9503041;
  // 3. XYZ → LAB (D65 white point)
  function f(t) {
    return t > 0.008856 ? Math.cbrt(t) : (7.787 * t) + 16 / 116;
  }
  const fx = f(X / 0.95047), fy = f(Y / 1.00000), fz = f(Z / 1.08883);
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)]; // [L, a, b]
}
function deltaE(lab1, lab2) {
  const dl = lab1[0]-lab2[0], da = lab1[1]-lab2[1], db = lab1[2]-lab2[2];
  return Math.sqrt(dl*dl + da*da + db*db);
}

// Washed colour: mix with white (wash) then slightly desaturate (sat < 1)
function washedCol(hex, wash, sat) {
  const [r,g,b] = hexRgb(hex);
  const wr = r + (255-r)*wash, wg = g + (255-g)*wash, wb = b + (255-b)*wash;
  const avg = (wr+wg+wb)/3;
  return `rgb(${clamp(avg+(wr-avg)*sat,0,255)|0},${clamp(avg+(wg-avg)*sat,0,255)|0},${clamp(avg+(wb-avg)*sat,0,255)|0})`;
}
// Irregular bleed: warp each column by a small random vertical drift,
// creating organic wavy band edges like fabric dye or watercolour
function applyDrift(ctx, physW, physH) {
  const img = ctx.getImageData(0, 0, physW, physH);
  const src = new Uint8ClampedArray(img.data);
  const d   = img.data;
  const ph1 = Math.random() * 6.28, ph2 = Math.random() * 6.28, ph3 = Math.random() * 6.28;
  const amp = physH / 300; // scale drift to canvas height
  for (let x = 0; x < physW; x++) {
    const t  = x / physW;
    const dy = Math.round(
      Math.sin(t * 6.28 * 2  + ph1) * 7 * amp +
      Math.sin(t * 6.28 * 5  + ph2) * 4 * amp +
      Math.sin(t * 6.28 * 13 + ph3) * 2 * amp
    );
    for (let y = 0; y < physH; y++) {
      const sy = Math.max(0, Math.min(physH - 1, y + dy));
      const di = (y  * physW + x) * 4;
      const si = (sy * physW + x) * 4;
      d[di]   = src[si];
      d[di+1] = src[si+1];
      d[di+2] = src[si+2];
      d[di+3] = src[si+3];
    }
  }
  ctx.putImageData(img, 0, 0);
}
// Carton texture: horizontal fiber bands (dye absorbed into cardboard)
// + fine per-pixel grain on top, giving a matte fibrous surface
function applyCartonTexture(ctx, physW, physH) {
  const img   = ctx.getImageData(0, 0, physW, physH);
  const d     = img.data;
  const FSIZE = 3; // physical pixels per fiber band (~1.5–3 CSS px depending on DPR)
  for (let fy = 0; fy < physH; fy += FSIZE) {
    const fiberN = (Math.random() - 0.5) * 18; // fiber-level brightness
    for (let y = fy; y < Math.min(fy + FSIZE, physH); y++) {
      for (let x = 0; x < physW; x++) {
        const i = (y * physW + x) * 4;
        const n = fiberN + (Math.random() - 0.5) * 26; // fine surface detail
        d[i]   = clamp(d[i]   + n, 0, 255);
        d[i+1] = clamp(d[i+1] + n, 0, 255);
        d[i+2] = clamp(d[i+2] + n, 0, 255);
      }
    }
  }
  ctx.putImageData(img, 0, 0);
}

// ─── Gradient strip renderer ─────────────────────────────────────────────────
function renderStrip(canvas, hexColors) {
  if (!hexColors.length) return;
  try {
    const dpr = window.devicePixelRatio || 1;
    const W   = Math.max(10,
                  canvas.offsetWidth ||
                  (canvas.parentElement && canvas.parentElement.offsetWidth) || 600);
    canvas.width  = Math.round(W * dpr);
    canvas.height = Math.round(EXPAND_H * dpr);
    canvas.style.width  = '100%';
    canvas.style.height = EXPAND_H + 'px';
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    const n = hexColors.length, BLEND = 0.44; // wide soft transitions
    const grad = ctx.createLinearGradient(0, 0, 0, EXPAND_H);
    hexColors.forEach((hex, i) => {
      const col = washedCol(hex, 0.15, 0.88); // 45% white wash, slight desaturate
      const y0 = i/n, y1 = (i+1)/n, bz = (y1-y0)*BLEND;
      grad.addColorStop(clamp(y0+bz,0,1), col);
      grad.addColorStop(clamp(y1-bz,0,1), col);
    });
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, EXPAND_H);
    // Irregular dye-bleed warp, then fine paper grain on top
    applyCartonTexture(ctx, canvas.width, canvas.height);
  } catch(e) { console.warn('renderStrip:', e); }
}

// ─── HSTV color space ────────────────────────────────────────────────────────
// Hue · Saturation · Temperature · Value
// Temperature separates warm (reds/oranges/yellows) from cool (blues/greens/purples)
function rgbToHstv(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r,g,b), min = Math.min(r,g,b);
  const v = max;
  const s = max === 0 ? 0 : (max - min) / max;
  let h = 0;
  if (max !== min) {
    const d = max - min;
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  h *= 360;
  // Temperature: peaks warm at 30° (orange), peaks cool at 210° (cyan-blue)
  const t = Math.cos((h - 30) * Math.PI / 180); // +1 warm, -1 cool
  return { h, s, t, v };
}

function hstvDistance(a, b) {
  // Circular hue distance (0–1)
  let dh = Math.abs(a.h - b.h);
  if (dh > 180) dh = 360 - dh;
  dh /= 180;
  // Weight hue by the lower saturation — desaturated pixels shouldn't be
  // steered by noisy hue readings
  const sMin = Math.min(a.s, b.s);
  const ds = Math.abs(a.s - b.s);
  const dt = Math.abs(a.t - b.t) / 2; // t range is 2 → normalise
  const dv = Math.abs(a.v - b.v);
  return Math.sqrt(dh*dh * 3 * sMin + ds*ds + dt*dt * 0.5 + dv*dv * 2);
}

// ─── Animation engine ────────────────────────────────────────────────────────
function createRevealAnimation(gradCanvas, photoEl, W, H, hexColors, assignments, paintEl, onDone, skipGradientFill, prefillCanvas, noBlobs, doReverse) {
  const { punchAssignments, blobAssignments, blobColorMap } = assignments;
  const sw  = gradCanvas.parentElement;
  const dpr = window.devicePixelRatio || 1;
  let rCanvas = null, rCurCanvas = null;

  function makeCanvas(zIndex) {
    const c = document.createElement('canvas');
    c.className = 'pal-anim-blob-canvas';
    c.width     = Math.round(W * dpr);
    c.height    = Math.round(H * dpr);
    c.style.cssText = `position:absolute;top:0;left:0;width:100%;height:${H}px;z-index:${zIndex};pointer-events:none;`;
    sw.appendChild(c);
    const ctx = c.getContext('2d');
    ctx.scale(dpr, dpr);
    return { canvas: c, ctx };
  }

  const { canvas: stitchCanvas, ctx: sCtx } = makeCanvas(4); // stitches accumulate
  const { canvas: cursorCanvas, ctx: cCtx } = makeCanvas(5); // cursor, cleared each frame

  if (prefillCanvas) {
    // Image-to-image transition: draw previous image onto stitch canvas as the cover layer.
    // Remove the temporary DOM cover — stitchCanvas now shows the same pixels.
    sCtx.drawImage(prefillCanvas, 0, 0, W, H);
    if (prefillCanvas.parentElement) prefillCanvas.remove();
    gradCanvas.style.transition = '';
    gradCanvas.style.opacity = '0';
  } else if (!skipGradientFill) {
    // Normal: fill stitch canvas with gradient, hide gradCanvas behind it
    const n = hexColors.length, BLEND = 0.35;
    const grad = sCtx.createLinearGradient(0, 0, 0, H);
    hexColors.forEach((hex, i) => {
      const col = washedCol(hex, 0.15, 0.88);
      const y0=i/n, y1=(i+1)/n, bz=(y1-y0)*BLEND;
      grad.addColorStop(clamp(y0+bz,0,1), col);
      grad.addColorStop(clamp(y1-bz,0,1), col);
    });
    sCtx.fillStyle = grad;
    sCtx.fillRect(0, 0, W, H);
    applyCartonTexture(sCtx, stitchCanvas.width, stitchCanvas.height);
    gradCanvas.style.transition = '';
    gradCanvas.style.opacity = '0'; // stitch canvas is now the visible gradient layer
  }
  // skipGradientFill=true (no prefillCanvas): stitch canvas stays transparent.

  // Offscreen canvas for pixel color sampling (CSS-pixel coords, no DPR scale)
  const offCanvas = document.createElement('canvas');
  offCanvas.width = W; offCanvas.height = H;
  const offCtx = offCanvas.getContext('2d', { willReadFrequently: true });
  try { drawCover(offCtx, photoEl, W, H); } catch(e) {}

  // 5×5 average sample — more area reduces single-pixel noise
  function sampleColor(ox, oy) {
    const data = offCtx.getImageData(Math.max(0, ox-2), Math.max(0, oy-2), 5, 5).data;
    let r=0, g=0, b=0, count=0;
    for (let i=0; i<data.length; i+=4) { r+=data[i]; g+=data[i+1]; b+=data[i+2]; count++; }
    return [Math.round(r/count), Math.round(g/count), Math.round(b/count)];
  }

  // ── Diagonal shadow — smooth NW→SE fade, steps of decreasing opacity ────────
  function diagShadow(bx, by, bw, bh, steps, maxOp) {
    for (let sd = 1; sd <= steps; sd++) {
      const op = maxOp * Math.pow(1 - sd / (steps + 1), 2.2);
      if (op < 0.01) continue;
      sCtx.fillStyle = `rgba(0,0,0,${op.toFixed(2)})`;
      // Fixed bh — no shrinking, just a clean diagonal offset that fades out
      sCtx.fillRect(bx + sd, by + sd, bw, bh);
    }
  }

  // ── Large area shadow — only during first 2 passes so it doesn't accumulate ──
  function areaShadow(cx, cy) {
    if (colorStep > 1) return; // only passes 0 and 1
    const RADIUS = 56;
    const cx2 = cx + SAMPLE_GRID / 2;
    const cy2 = cy + SAMPLE_GRID / 2;
    for (let dy = -RADIUS; dy <= RADIUS; dy += 4) {
      for (let dx = -RADIUS; dx <= RADIUS; dx += 4) {
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > RADIUS) continue;
        const op = 0.045 * Math.pow(1 - dist / RADIUS, 1.8);
        if (op < 0.003) continue;
        sCtx.fillStyle = `rgba(0,0,0,${op.toFixed(3)})`;
        sCtx.fillRect(Math.round(cx2 + dx), Math.round(cy2 + dy), 4, 4);
      }
    }
  }

  // ── Pixel punch — clearRect through gradient + organic halo ─────────────────
  // Sorted by hue → value (dark first) → saturation (low first)
  function doPunch(x, y) {
    // noBlobs=true (Tidal): clean punch only — no area shadow, no diagonal shadow, no grain
    const shadowsOn = !noBlobs && (performance.now() - animStart) < 5000;

    if (shadowsOn) areaShadow(x, y);

    // Row-by-row clearRect — wider overlap to erase adjacent shadow residue
    for (let dy = -1; dy < SAMPLE_GRID + 1; dy++) {
      const xOff = Math.round((Math.random() - 0.5) * 2);
      const wVar = SAMPLE_GRID + 6 + Math.floor((Math.random() - 0.5) * 2);
      sCtx.clearRect(x - 3 + xOff, y + dy, wVar, 1);
    }

    if (shadowsOn) diagShadow(x, y, SAMPLE_GRID, SAMPLE_GRID, 6, 0.38);

    // Destination-out fray — extended reach to clear neighbour shadow residue
    sCtx.save();
    sCtx.globalCompositeOperation = 'destination-out';
    for (let dy = -8; dy < SAMPLE_GRID + 8; dy++) {
      const inCore = dy >= 0 && dy < SAMPLE_GRID;
      const dist   = inCore ? 0 : (dy < 0 ? -(dy + 1) : dy - SAMPLE_GRID);
      const baseOp = inCore
        ? 0.14 + Math.random() * 0.08
        : 0.55 * Math.pow(1 - dist / 8, 1.6) * (0.6 + Math.random() * 0.8);
      if (baseOp < 0.02) continue;
      const xEdge = Math.round((Math.random() - 0.5) * 6);
      const wEdge = SAMPLE_GRID + 10 + Math.round((Math.random() - 0.5) * 6);
      sCtx.fillStyle = `rgba(0,0,0,${Math.min(baseOp, 0.6).toFixed(2)})`;
      sCtx.fillRect(x - 5 + xEdge, y + dy, wEdge, 1);
    }
    sCtx.restore();

    if (!noBlobs) {
      sCtx.fillStyle = 'rgba(0,0,0,0.12)';
      sCtx.fillRect(x + Math.floor(Math.random() * SAMPLE_GRID), y + Math.floor(Math.random() * SAMPLE_GRID), 1, 1);
      sCtx.fillStyle = 'rgba(255,255,255,0.07)';
      sCtx.fillRect(x, y, SAMPLE_GRID, 1);
    }
  }

  // ── Paint blob — identical shape/size/texture as punch, painted ON gradient ──
  // Sorted by hue → saturation → value
  function doBlob(x, y) {
    // Use the nearest palette hex colour for this position (exact palette match)
    const gx = Math.floor(x / SAMPLE_GRID) * SAMPLE_GRID;
    const gy = Math.floor(y / SAMPLE_GRID) * SAMPLE_GRID;
    const palCol = blobColorMap && blobColorMap.get(`${gx},${gy}`);
    const [r, g, b] = palCol || sampleColor(x + Math.floor(SAMPLE_GRID / 2), y + Math.floor(SAMPLE_GRID / 2));

    // Large area shadow (first 2 passes only)
    areaShadow(x, y);

    // Diagonal shadow fading outward — same as punch
    diagShadow(x, y, SAMPLE_GRID, SAMPLE_GRID, 6, 0.38);

    // Original size, strong painterly glow
    sCtx.save();
    const blurAmt = 10 + Math.random() * 14;  // 10–24px blur for strong painterly glow
    sCtx.shadowColor = `rgba(${r},${g},${b},0.75)`;
    sCtx.shadowBlur  = blurAmt;
    for (let dy = 0; dy < SAMPLE_GRID; dy++) {
      const alpha = 0.28 + Math.random() * 0.12;
      const xOff  = Math.round((Math.random() - 0.5) * 2);
      const wVar  = SAMPLE_GRID - 1 + Math.floor(Math.random() * 2);
      sCtx.fillStyle = `rgba(${r},${g},${b},${alpha})`;
      sCtx.fillRect(x + xOff, y + dy, wVar, 1);
    }
    sCtx.restore();
    // Grain
    sCtx.fillStyle = 'rgba(0,0,0,0.10)';
    sCtx.fillRect(x + Math.floor(Math.random() * SAMPLE_GRID), y + Math.floor(Math.random() * SAMPLE_GRID), 1, 1);
    // Thread highlight
    sCtx.fillStyle = 'rgba(255,255,255,0.09)';
    sCtx.fillRect(x, y, SAMPLE_GRID, 1);
  }

  // ── Cursor draw ─────────────────────────────────────────────────────────────
  const GLOW_R = 32; // glow radius in px
  let prevCX = -1, prevCY = -1;
  function drawCursor(x, y) {
    // Clear previous glow + cursor area
    if (prevCX >= 0) cCtx.clearRect(prevCX - GLOW_R - 1, prevCY - GLOW_R - 1, GLOW_R * 2 + 4, GLOW_R * 2 + 4);
    // Radial glow centered on cursor
    const grd = cCtx.createRadialGradient(x, y, 0, x, y, GLOW_R);
    grd.addColorStop(0,   'rgba(255,245,220,0.28)');
    grd.addColorStop(0.4, 'rgba(255,235,180,0.12)');
    grd.addColorStop(1,   'rgba(255,220,140,0)');
    cCtx.fillStyle = grd;
    cCtx.fillRect(x - GLOW_R, y - GLOW_R, GLOW_R * 2, GLOW_R * 2);
    // Cursor line
    cCtx.fillStyle = 'rgba(255,255,255,0.95)';
    cCtx.fillRect(x, y - 7, 1, 14);
    prevCX = x; prevCY = y;
  }
  function clearCursor() {
    if (prevCX >= 0) cCtx.clearRect(prevCX - GLOW_R - 1, prevCY - GLOW_R - 1, GLOW_R * 2 + 4, GLOW_R * 2 + 4);
    prevCX = -1;
  }

  // ── Sweep state ─────────────────────────────────────────────────────────────
  const SPEED     = W / ROW_CROSS_MS;
  const totalRows = Math.floor(H / SAMPLE_GRID);
  const animStart = performance.now(); // track time to disable shadows after 5s

  let colorStep = 0;   // current palette color index
  let rowIdx    = 0;   // current row (0 = top)
  let headX     = 0;   // cursor x (CSS px)
  let ltr       = true;
  let lastX     = -SAMPLE_GRID; // last stitch-checked x

  function resetToColor(step) {
    colorStep = step;
    rowIdx    = 0;
    ltr       = true;
    headX     = 0;
    lastX     = -SAMPLE_GRID;
  }

  function advanceRow() {
    rowIdx++;
    ltr   = !ltr;
    headX = ltr ? 0 : W;
    lastX = ltr ? -SAMPLE_GRID : W;
  }

  // ── State machine ─────────────────────────────────────────────────────────
  let lastNow   = 0;
  let stopped   = false;
  let raf       = null;
  let holdTimer = null;

  function cancel() {
    if (raf)       { cancelAnimationFrame(raf); raf = null; }
    if (holdTimer) { clearTimeout(holdTimer); holdTimer = null; }
  }

  function loop(now) {
    if (stopped) return;
    const dt = lastNow ? Math.min(now - lastNow, 50) : 16;
    lastNow = now;

    // ── REVEAL ──────────────────────────────────────────────────────────────
    {
      const rowY = rowIdx * SAMPLE_GRID;
      const adv  = SPEED * dt;

      if (ltr) {
        headX = Math.min(W, headX + adv);
        while (lastX + SAMPLE_GRID <= headX) {
          lastX += SAMPLE_GRID;
          if (lastX >= 0 && lastX < W) {
            const gx = Math.floor(lastX / SAMPLE_GRID) * SAMPLE_GRID;
            const gy = Math.floor(rowY  / SAMPLE_GRID) * SAMPLE_GRID;
            const key = `${gx},${gy}`;
            // Blob paints under every matched position; punch cuts through on top
            if (!noBlobs && blobAssignments.get(key) === colorStep) doBlob(lastX, rowY);
            if (punchAssignments.get(key) === colorStep) doPunch(lastX, rowY);
          }
        }
      } else {
        headX = Math.max(0, headX - adv);
        while (lastX - SAMPLE_GRID >= headX) {
          lastX -= SAMPLE_GRID;
          if (lastX >= 0 && lastX < W) {
            const gx = Math.floor(lastX / SAMPLE_GRID) * SAMPLE_GRID;
            const gy = Math.floor(rowY  / SAMPLE_GRID) * SAMPLE_GRID;
            const key = `${gx},${gy}`;
            if (!noBlobs && blobAssignments.get(key) === colorStep) doBlob(lastX, rowY);
            if (punchAssignments.get(key) === colorStep) doPunch(lastX, rowY);
          }
        }
      }

      drawCursor(Math.round(headX), rowY);

      const rowDone = ltr ? headX >= W : headX <= 0;
      if (rowDone) {
        if (rowIdx + 1 >= totalRows) {
          // This pass complete — pause then next pass or finish
          colorStep++;
          clearCursor();
          if (colorStep >= Math.floor(LOOM_PASSES * 0.9)) {
            photoEl.style.transition = 'filter 3800ms ease-out';
            photoEl.style.filter = 'blur(0px) saturate(1) contrast(1)';
          }
          if (colorStep < LOOM_PASSES) {
            // Flash cursor at top-left so user sees the reset
            cCtx.clearRect(0, 0, W, H);
            cCtx.fillStyle = 'rgba(255,255,255,0.9)';
            cCtx.fillRect(0, 0, 1, SAMPLE_GRID + 4);
            holdTimer = setTimeout(() => {
              if (stopped) return;
              cCtx.clearRect(0, 0, W, H);
              resetToColor(colorStep);
              lastNow = 0;
              raf = requestAnimationFrame(loop);
            }, PAUSE_MS);
          } else {
            // Outro: sequentially punch through blob positions in the same
            // h→s→v order they were painted, one pass group at a time.
            // Punch positions are already transparent — only blobs need clearing.
            cancel();

            let oColorStep = 0, oRow = 0, oLtr = true, oHeadX = 0, oLastX = -SAMPLE_GRID;
            let oLastNow   = 0;

            function outroLoop(now) {
              if (stopped) return;
              const dt = oLastNow ? Math.min(now - oLastNow, 50) : 16;
              oLastNow = now;

              const rowY = oRow * SAMPLE_GRID;
              const adv  = SPEED * dt;

              if (oLtr) {
                oHeadX = Math.min(W, oHeadX + adv);
                while (oLastX + SAMPLE_GRID <= oHeadX) {
                  oLastX += SAMPLE_GRID;
                  if (oLastX >= 0 && oLastX < W) {
                    const gx  = Math.floor(oLastX / SAMPLE_GRID) * SAMPLE_GRID;
                    const gy  = Math.floor(rowY   / SAMPLE_GRID) * SAMPLE_GRID;
                    if (blobAssignments.get(`${gx},${gy}`) === oColorStep) {
                      sCtx.clearRect(oLastX - 1, rowY - 1, SAMPLE_GRID + 2, SAMPLE_GRID + 2);
                    }
                  }
                }
              } else {
                oHeadX = Math.max(0, oHeadX - adv);
                while (oLastX - SAMPLE_GRID >= oHeadX) {
                  oLastX -= SAMPLE_GRID;
                  if (oLastX >= 0 && oLastX < W) {
                    const gx  = Math.floor(oLastX / SAMPLE_GRID) * SAMPLE_GRID;
                    const gy  = Math.floor(rowY   / SAMPLE_GRID) * SAMPLE_GRID;
                    if (blobAssignments.get(`${gx},${gy}`) === oColorStep) {
                      sCtx.clearRect(oLastX - 1, rowY - 1, SAMPLE_GRID + 2, SAMPLE_GRID + 2);
                    }
                  }
                }
              }

              drawCursor(Math.round(oHeadX), rowY);

              const rowDone = oLtr ? oHeadX >= W : oHeadX <= 0;
              if (rowDone) {
                if (oRow + 1 >= totalRows) {
                  // This blob group done — move to next group
                  oColorStep++;
                  if (oColorStep < LOOM_PASSES) {
                    oRow = 0; oLtr = true; oHeadX = 0; oLastX = -SAMPLE_GRID;
                    oLastNow = 0;
                  } else {
                    // All blob groups punched — fade stitch canvas, then hand off
                    clearCursor();
                    cursorCanvas.remove();
                    stitchCanvas.style.transition = 'opacity 1000ms ease';
                    stitchCanvas.style.opacity = '0';
                    stitchCanvas.addEventListener('transitionend', () => {
                      stitchCanvas.remove();
                      if (doReverse) {
                        holdTimer = setTimeout(startTidalReverse, 1500);
                      } else if (typeof onDone === 'function') {
                        onDone();
                      }
                    }, { once: true });
                    return;
                  }
                } else {
                  oRow++;
                  oLtr   = !oLtr;
                  oHeadX = oLtr ? 0 : W;
                  oLastX = oLtr ? -SAMPLE_GRID : W;
                }
              }

              raf = requestAnimationFrame(outroLoop);
            }

            raf = requestAnimationFrame(outroLoop);
          }
          return;
        } else {
          advanceRow();
        }
      }

      raf = requestAnimationFrame(loop);
      return;
    }
  }

  function startTidalReverse() {
    if (stopped) return;
    const { canvas: rc, ctx: rCtx } = makeCanvas(4);
    const { canvas: rcc, ctx: rCCtx } = makeCanvas(5);
    rCanvas = rc; rCurCanvas = rcc;

    let rPrevCX = -1, rPrevCY = -1;
    function drawRCursor(x, y) {
      if (rPrevCX >= 0) rCCtx.clearRect(rPrevCX - GLOW_R - 1, rPrevCY - GLOW_R - 1, GLOW_R * 2 + 4, GLOW_R * 2 + 4);
      const grd = rCCtx.createRadialGradient(x, y, 0, x, y, GLOW_R);
      grd.addColorStop(0,   'rgba(255,245,220,0.28)');
      grd.addColorStop(0.4, 'rgba(255,235,180,0.12)');
      grd.addColorStop(1,   'rgba(255,220,140,0)');
      rCCtx.fillStyle = grd;
      rCCtx.fillRect(x - GLOW_R, y - GLOW_R, GLOW_R * 2, GLOW_R * 2);
      rCCtx.fillStyle = 'rgba(255,255,255,0.95)';
      rCCtx.fillRect(x, y - 7, 1, 14);
      rPrevCX = x; rPrevCY = y;
    }
    function clearRCursor() {
      if (rPrevCX >= 0) rCCtx.clearRect(rPrevCX - GLOW_R - 1, rPrevCY - GLOW_R - 1, GLOW_R * 2 + 4, GLOW_R * 2 + 4);
      rPrevCX = -1;
    }

    function restoreCell(gx, gy) {
      // Core strip — mirrors the fray shape of doPunch
      for (let dy = -1; dy < SAMPLE_GRID + 1; dy++) {
        const xOff = Math.round((Math.random() - 0.5) * 2);
        const wVar = SAMPLE_GRID + 6 + Math.floor((Math.random() - 0.5) * 2);
        const dstX = gx - 3 + xOff, dstY = gy + dy;
        const sx   = Math.max(0, Math.round(dstX * dpr));
        const sy   = Math.round(dstY * dpr);
        const sw2  = Math.round(wVar * dpr);
        const sh2  = Math.max(1, Math.round(dpr));
        if (sy < 0 || sy + sh2 > gradCanvas.height || sx + sw2 > gradCanvas.width) continue;
        rCtx.drawImage(gradCanvas, sx, sy, sw2, sh2, dstX, dstY, wVar, 1);
      }
      // Fibrous frayed edges
      for (let dy = -8; dy < SAMPLE_GRID + 8; dy++) {
        const inCore = dy >= 0 && dy < SAMPLE_GRID;
        if (inCore) continue;
        const dist   = dy < 0 ? -(dy + 1) : dy - SAMPLE_GRID;
        const baseOp = 0.55 * Math.pow(1 - dist / 8, 1.6) * (0.6 + Math.random() * 0.8);
        if (baseOp < 0.02) continue;
        const xEdge = Math.round((Math.random() - 0.5) * 6);
        const wEdge = SAMPLE_GRID + 10 + Math.round((Math.random() - 0.5) * 6);
        const dstX2 = gx - 5 + xEdge, dstY2 = gy + dy;
        const sx2   = Math.max(0, Math.round(dstX2 * dpr));
        const sy2   = Math.round(dstY2 * dpr);
        const sw3   = Math.round(wEdge * dpr);
        const sh3   = Math.max(1, Math.round(dpr));
        if (sy2 < 0 || sy2 + sh3 > gradCanvas.height || sx2 + sw3 > gradCanvas.width) continue;
        rCtx.save();
        rCtx.globalAlpha = Math.min(baseOp, 0.6);
        rCtx.drawImage(gradCanvas, sx2, sy2, sw3, sh3, dstX2, dstY2, wEdge, 1);
        rCtx.restore();
      }
      rCtx.fillStyle = 'rgba(0,0,0,0.12)';
      rCtx.fillRect(gx + Math.floor(Math.random() * SAMPLE_GRID), gy + Math.floor(Math.random() * SAMPLE_GRID), 1, 1);
      rCtx.fillStyle = 'rgba(255,255,255,0.07)';
      rCtx.fillRect(gx, gy, SAMPLE_GRID, 1);
    }

    let rColorStep = LOOM_PASSES - 1;
    let rRow = totalRows - 1, rLtr = false, rHeadX = W, rLastX = W, rLastNow = 0;

    function reverseLoop(now) {
      if (stopped) return;
      const dt = rLastNow ? Math.min(now - rLastNow, 50) : 16;
      rLastNow = now;
      const rowY = rRow * SAMPLE_GRID;
      const adv  = SPEED * dt;
      if (rLtr) {
        rHeadX = Math.min(W, rHeadX + adv);
        while (rLastX + SAMPLE_GRID <= rHeadX) {
          rLastX += SAMPLE_GRID;
          if (rLastX >= 0 && rLastX < W) {
            const gx = Math.floor(rLastX / SAMPLE_GRID) * SAMPLE_GRID;
            const gy = Math.floor(rowY   / SAMPLE_GRID) * SAMPLE_GRID;
            const key = `${gx},${gy}`;
            if (punchAssignments.get(key) === rColorStep || blobAssignments.get(key) === rColorStep) restoreCell(gx, gy);
          }
        }
      } else {
        rHeadX = Math.max(0, rHeadX - adv);
        while (rLastX - SAMPLE_GRID >= rHeadX) {
          rLastX -= SAMPLE_GRID;
          if (rLastX >= 0 && rLastX < W) {
            const gx = Math.floor(rLastX / SAMPLE_GRID) * SAMPLE_GRID;
            const gy = Math.floor(rowY   / SAMPLE_GRID) * SAMPLE_GRID;
            const key = `${gx},${gy}`;
            if (punchAssignments.get(key) === rColorStep || blobAssignments.get(key) === rColorStep) restoreCell(gx, gy);
          }
        }
      }
      drawRCursor(Math.round(rHeadX), rowY);
      const rowDone = rLtr ? rHeadX >= W : rHeadX <= 0;
      if (rowDone) {
        if (rRow <= 0) {
          rColorStep--;
          if (rColorStep >= 0) {
            rRow = totalRows - 1; rLtr = false; rHeadX = W; rLastX = W; rLastNow = 0;
          } else {
            // Reverse complete — rCanvas now fully covers the canvas with gradient.
            // Fade gradCanvas in behind it, then remove rCanvas cleanly.
            clearRCursor();
            rCurCanvas.remove();
            rCurCanvas = null;
            gradCanvas.style.transition = 'opacity 800ms ease';
            gradCanvas.style.opacity = '1';
            holdTimer = setTimeout(() => { if (!stopped && rCanvas) { rCanvas.remove(); rCanvas = null; } }, 850);
            return;
          }
        } else {
          rRow--; rLtr = !rLtr;
          rHeadX = rLtr ? 0 : W;
          rLastX = rLtr ? -SAMPLE_GRID : W;
        }
      }
      raf = requestAnimationFrame(reverseLoop);
    }

    raf = requestAnimationFrame(reverseLoop);
  }

  photoEl.style.transition = '';
  photoEl.style.filter = 'blur(13px) saturate(1.25) contrast(1.1)';

  holdTimer = setTimeout(() => {
    if (stopped) return;
    resetToColor(0);
    lastNow = 0;
    raf = requestAnimationFrame(loop);
  }, HOLD_MS);

  return {
    stop() {
      stopped = true;
      cancel();
      stitchCanvas.remove();
      cursorCanvas.remove();
      if (rCanvas)    { rCanvas.remove();    rCanvas = null; }
      if (rCurCanvas) { rCurCanvas.remove(); rCurCanvas = null; }
      photoEl.style.transition = '';
      photoEl.style.filter = '';
      if (paintEl) paintEl.remove();
    }
  };
}

// ─── Orchard animation — forward loom + reverse loom (final1withreverse) ─────
function createOrchardAnimation(gradCanvas, photoEl, W, H, hexColors, assignments, paintEl) {
  const { punchAssignments, blobAssignments, blobColorMap } = assignments;
  const sw  = gradCanvas.parentElement;
  const dpr = window.devicePixelRatio || 1;

  function makeCanvas(zIndex) {
    const c = document.createElement('canvas');
    c.className = 'pal-anim-blob-canvas';
    c.width     = Math.round(W * dpr);
    c.height    = Math.round(H * dpr);
    c.style.cssText = `position:absolute;top:0;left:0;width:100%;height:${H}px;z-index:${zIndex};pointer-events:none;`;
    sw.appendChild(c);
    const ctx = c.getContext('2d');
    ctx.scale(dpr, dpr);
    return { canvas: c, ctx };
  }

  const { canvas: stitchCanvas, ctx: sCtx } = makeCanvas(4);
  const { canvas: cursorCanvas, ctx: cCtx } = makeCanvas(5);

  {
    const n = hexColors.length, BLEND = 0.35;
    const grad = sCtx.createLinearGradient(0, 0, 0, H);
    hexColors.forEach((hex, i) => {
      const col = washedCol(hex, 0.15, 0.88);
      const y0=i/n, y1=(i+1)/n, bz=(y1-y0)*BLEND;
      grad.addColorStop(clamp(y0+bz,0,1), col);
      grad.addColorStop(clamp(y1-bz,0,1), col);
    });
    sCtx.fillStyle = grad;
    sCtx.fillRect(0, 0, W, H);
    applyCartonTexture(sCtx, stitchCanvas.width, stitchCanvas.height);
  }
  gradCanvas.style.opacity = '0';

  const offCanvas = document.createElement('canvas');
  offCanvas.width = W; offCanvas.height = H;
  const offCtx = offCanvas.getContext('2d', { willReadFrequently: true });
  try { drawCover(offCtx, photoEl, W, H); } catch(e) {}

  function sampleColor(ox, oy) {
    const data = offCtx.getImageData(Math.max(0, ox-2), Math.max(0, oy-2), 5, 5).data;
    let r=0, g=0, b=0, count=0;
    for (let i=0; i<data.length; i+=4) { r+=data[i]; g+=data[i+1]; b+=data[i+2]; count++; }
    return [Math.round(r/count), Math.round(g/count), Math.round(b/count)];
  }

  function diagShadow(bx, by, bw, bh, steps, maxOp) {
    for (let sd = 1; sd <= steps; sd++) {
      const op = maxOp * Math.pow(1 - sd / (steps + 1), 2.2);
      if (op < 0.01) continue;
      sCtx.fillStyle = `rgba(0,0,0,${op.toFixed(2)})`;
      sCtx.fillRect(bx + sd, by + sd, bw, bh);
    }
  }

  function areaShadow(cx, cy) {
    if (colorStep > 1) return;
    const RADIUS = 56;
    const cx2 = cx + SAMPLE_GRID / 2;
    const cy2 = cy + SAMPLE_GRID / 2;
    for (let dy = -RADIUS; dy <= RADIUS; dy += 4) {
      for (let dx = -RADIUS; dx <= RADIUS; dx += 4) {
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > RADIUS) continue;
        const op = 0.045 * Math.pow(1 - dist / RADIUS, 1.8);
        if (op < 0.003) continue;
        sCtx.fillStyle = `rgba(0,0,0,${op.toFixed(3)})`;
        sCtx.fillRect(Math.round(cx2 + dx), Math.round(cy2 + dy), 4, 4);
      }
    }
  }

  function doPunch(x, y) {
    const [r, g, b] = sampleColor(x + Math.floor(SAMPLE_GRID / 2), y + Math.floor(SAMPLE_GRID / 2));
    const shadowsOn = (performance.now() - animStart) < 5000;
    if (shadowsOn) areaShadow(x, y);
    for (let dy = -1; dy < SAMPLE_GRID + 1; dy++) {
      const xOff = Math.round((Math.random() - 0.5) * 2);
      const wVar = SAMPLE_GRID + 6 + Math.floor((Math.random() - 0.5) * 2);
      sCtx.clearRect(x - 3 + xOff, y + dy, wVar, 1);
    }
    if (shadowsOn) diagShadow(x, y, SAMPLE_GRID, SAMPLE_GRID, 6, 0.38);
    sCtx.save();
    sCtx.globalCompositeOperation = 'destination-out';
    for (let dy = -8; dy < SAMPLE_GRID + 8; dy++) {
      const inCore = dy >= 0 && dy < SAMPLE_GRID;
      const dist   = inCore ? 0 : (dy < 0 ? -(dy + 1) : dy - SAMPLE_GRID);
      const baseOp = inCore
        ? 0.14 + Math.random() * 0.08
        : 0.55 * Math.pow(1 - dist / 8, 1.6) * (0.6 + Math.random() * 0.8);
      if (baseOp < 0.02) continue;
      const xEdge = Math.round((Math.random() - 0.5) * 6);
      const wEdge = SAMPLE_GRID + 10 + Math.round((Math.random() - 0.5) * 6);
      sCtx.fillStyle = `rgba(0,0,0,${Math.min(baseOp, 0.6).toFixed(2)})`;
      sCtx.fillRect(x - 5 + xEdge, y + dy, wEdge, 1);
    }
    sCtx.restore();
    sCtx.fillStyle = 'rgba(0,0,0,0.12)';
    sCtx.fillRect(x + Math.floor(Math.random() * SAMPLE_GRID), y + Math.floor(Math.random() * SAMPLE_GRID), 1, 1);
    sCtx.fillStyle = 'rgba(255,255,255,0.07)';
    sCtx.fillRect(x, y, SAMPLE_GRID, 1);
  }

  function doBlob(x, y) {
    const gx = Math.floor(x / SAMPLE_GRID) * SAMPLE_GRID;
    const gy = Math.floor(y / SAMPLE_GRID) * SAMPLE_GRID;
    const palCol = blobColorMap && blobColorMap.get(`${gx},${gy}`);
    const [r, g, b] = palCol || sampleColor(x + Math.floor(SAMPLE_GRID / 2), y + Math.floor(SAMPLE_GRID / 2));
    areaShadow(x, y);
    diagShadow(x, y, SAMPLE_GRID, SAMPLE_GRID, 6, 0.38);
    sCtx.save();
    const blurAmt = 6 + Math.random() * 8;
    sCtx.shadowColor = `rgba(${r},${g},${b},0.55)`;
    sCtx.shadowBlur  = blurAmt;
    for (let dy = 0; dy < SAMPLE_GRID; dy++) {
      const alpha = 0.52 + Math.random() * 0.14;
      const xOff  = Math.round((Math.random() - 0.5) * 2);
      const wVar  = SAMPLE_GRID - 1 + Math.floor(Math.random() * 2);
      sCtx.fillStyle = `rgba(${r},${g},${b},${alpha})`;
      sCtx.fillRect(x + xOff, y + dy, wVar, 1);
    }
    sCtx.restore();
    sCtx.fillStyle = 'rgba(0,0,0,0.10)';
    sCtx.fillRect(x + Math.floor(Math.random() * SAMPLE_GRID), y + Math.floor(Math.random() * SAMPLE_GRID), 1, 1);
    sCtx.fillStyle = 'rgba(255,255,255,0.09)';
    sCtx.fillRect(x, y, SAMPLE_GRID, 1);
  }

  const GLOW_R = 32;
  let prevCX = -1, prevCY = -1;
  function drawCursor(x, y) {
    if (prevCX >= 0) cCtx.clearRect(prevCX - GLOW_R - 1, prevCY - GLOW_R - 1, GLOW_R * 2 + 4, GLOW_R * 2 + 4);
    const grd = cCtx.createRadialGradient(x, y, 0, x, y, GLOW_R);
    grd.addColorStop(0,   'rgba(255,245,220,0.28)');
    grd.addColorStop(0.4, 'rgba(255,235,180,0.12)');
    grd.addColorStop(1,   'rgba(255,220,140,0)');
    cCtx.fillStyle = grd;
    cCtx.fillRect(x - GLOW_R, y - GLOW_R, GLOW_R * 2, GLOW_R * 2);
    cCtx.fillStyle = 'rgba(255,255,255,0.95)';
    cCtx.fillRect(x, y - 7, 1, 14);
    prevCX = x; prevCY = y;
  }
  function clearCursor() {
    if (prevCX >= 0) cCtx.clearRect(prevCX - GLOW_R - 1, prevCY - GLOW_R - 1, GLOW_R * 2 + 4, GLOW_R * 2 + 4);
    prevCX = -1;
  }

  const SPEED     = W / ROW_CROSS_MS;
  const totalRows = Math.floor(H / SAMPLE_GRID);
  const animStart = performance.now();
  const PASSES    = ORCHARD_PASSES;

  let colorStep = 0, rowIdx = 0, headX = 0, ltr = true, lastX = -SAMPLE_GRID;

  function resetToColor(step) { colorStep = step; rowIdx = 0; ltr = true; headX = 0; lastX = -SAMPLE_GRID; }
  function advanceRow() { rowIdx++; ltr = !ltr; headX = ltr ? 0 : W; lastX = ltr ? -SAMPLE_GRID : W; }

  let lastNow = 0, stopped = false, raf = null, holdTimer = null;
  function cancel() {
    if (raf)       { cancelAnimationFrame(raf); raf = null; }
    if (holdTimer) { clearTimeout(holdTimer); holdTimer = null; }
  }

  function loop(now) {
    if (stopped) return;
    const dt = lastNow ? Math.min(now - lastNow, 50) : 16;
    lastNow = now;
    {
      const rowY = rowIdx * SAMPLE_GRID;
      const adv  = SPEED * dt;
      if (ltr) {
        headX = Math.min(W, headX + adv);
        while (lastX + SAMPLE_GRID <= headX) {
          lastX += SAMPLE_GRID;
          if (lastX >= 0 && lastX < W) {
            const gx = Math.floor(lastX / SAMPLE_GRID) * SAMPLE_GRID;
            const gy = Math.floor(rowY  / SAMPLE_GRID) * SAMPLE_GRID;
            if (punchAssignments.get(`${gx},${gy}`) === colorStep) doPunch(lastX, rowY);
            else if (blobAssignments.get(`${gx},${gy}`) === colorStep) doBlob(lastX, rowY);
          }
        }
      } else {
        headX = Math.max(0, headX - adv);
        while (lastX - SAMPLE_GRID >= headX) {
          lastX -= SAMPLE_GRID;
          if (lastX >= 0 && lastX < W) {
            const gx = Math.floor(lastX / SAMPLE_GRID) * SAMPLE_GRID;
            const gy = Math.floor(rowY  / SAMPLE_GRID) * SAMPLE_GRID;
            if (punchAssignments.get(`${gx},${gy}`) === colorStep) doPunch(lastX, rowY);
            else if (blobAssignments.get(`${gx},${gy}`) === colorStep) doBlob(lastX, rowY);
          }
        }
      }
      drawCursor(Math.round(headX), rowY);
      const rowDone = ltr ? headX >= W : headX <= 0;
      if (rowDone) {
        if (rowIdx + 1 >= totalRows) {
          colorStep++;
          clearCursor();
          if (colorStep < PASSES) {
            cCtx.clearRect(0, 0, W, H);
            cCtx.fillStyle = 'rgba(255,255,255,0.9)';
            cCtx.fillRect(0, 0, 1, SAMPLE_GRID + 4);
            holdTimer = setTimeout(() => {
              if (stopped) return;
              cCtx.clearRect(0, 0, W, H);
              resetToColor(colorStep);
              lastNow = 0;
              raf = requestAnimationFrame(loop);
            }, PAUSE_MS);
          } else {
            cancel();
            photoEl.style.transition = 'filter 3000ms ease';
            void photoEl.getBoundingClientRect();
            photoEl.style.filter = 'blur(0px)';

            let oColorStep = 0, oRow = 0, oLtr = true, oHeadX = 0, oLastX = -SAMPLE_GRID, oLastNow = 0;
            function forwardOutro(now) {
              if (stopped) return;
              const dt = oLastNow ? Math.min(now - oLastNow, 50) : 16;
              oLastNow = now;
              const rowY = oRow * SAMPLE_GRID;
              const adv  = SPEED * dt;
              if (oLtr) {
                oHeadX = Math.min(W, oHeadX + adv);
                while (oLastX + SAMPLE_GRID <= oHeadX) {
                  oLastX += SAMPLE_GRID;
                  if (oLastX >= 0 && oLastX < W) {
                    const gx = Math.floor(oLastX / SAMPLE_GRID) * SAMPLE_GRID;
                    const gy = Math.floor(rowY   / SAMPLE_GRID) * SAMPLE_GRID;
                    if (blobAssignments.get(`${gx},${gy}`) === oColorStep)
                      sCtx.clearRect(oLastX - 1, rowY - 1, SAMPLE_GRID + 2, SAMPLE_GRID + 2);
                  }
                }
              } else {
                oHeadX = Math.max(0, oHeadX - adv);
                while (oLastX - SAMPLE_GRID >= oHeadX) {
                  oLastX -= SAMPLE_GRID;
                  if (oLastX >= 0 && oLastX < W) {
                    const gx = Math.floor(oLastX / SAMPLE_GRID) * SAMPLE_GRID;
                    const gy = Math.floor(rowY   / SAMPLE_GRID) * SAMPLE_GRID;
                    if (blobAssignments.get(`${gx},${gy}`) === oColorStep)
                      sCtx.clearRect(oLastX - 1, rowY - 1, SAMPLE_GRID + 2, SAMPLE_GRID + 2);
                  }
                }
              }
              drawCursor(Math.round(oHeadX), rowY);
              const rowDone = oLtr ? oHeadX >= W : oHeadX <= 0;
              if (rowDone) {
                if (oRow + 1 >= totalRows) {
                  oColorStep++;
                  if (oColorStep < PASSES) {
                    oRow = 0; oLtr = true; oHeadX = 0; oLastX = -SAMPLE_GRID; oLastNow = 0;
                  } else {
                    holdTimer = setTimeout(startReverseOutro, 1500);
                    return;
                  }
                } else {
                  oRow++; oLtr = !oLtr;
                  oHeadX = oLtr ? 0 : W;
                  oLastX = oLtr ? -SAMPLE_GRID : W;
                }
              }
              raf = requestAnimationFrame(forwardOutro);
            }

            function startReverseOutro() {
              if (stopped) return;
              function restoreCell(gx, gy) {
                for (let dy = -1; dy < SAMPLE_GRID + 1; dy++) {
                  const xOff = Math.round((Math.random() - 0.5) * 2);
                  const wVar = SAMPLE_GRID + 6 + Math.floor((Math.random() - 0.5) * 2);
                  const dstX = gx - 3 + xOff, dstY = gy + dy;
                  const sx   = Math.max(0, Math.round(dstX * dpr));
                  const sy   = Math.round(dstY * dpr);
                  const sw   = Math.round(wVar * dpr);
                  const sh   = Math.max(1, Math.round(dpr));
                  if (sy < 0 || sy + sh > gradCanvas.height || sx + sw > gradCanvas.width) continue;
                  sCtx.drawImage(gradCanvas, sx, sy, sw, sh, dstX, dstY, wVar, 1);
                }
                for (let dy = -8; dy < SAMPLE_GRID + 8; dy++) {
                  const inCore = dy >= 0 && dy < SAMPLE_GRID;
                  if (inCore) continue;
                  const dist   = dy < 0 ? -(dy + 1) : dy - SAMPLE_GRID;
                  const baseOp = 0.55 * Math.pow(1 - dist / 8, 1.6) * (0.6 + Math.random() * 0.8);
                  if (baseOp < 0.02) continue;
                  const xEdge = Math.round((Math.random() - 0.5) * 6);
                  const wEdge = SAMPLE_GRID + 10 + Math.round((Math.random() - 0.5) * 6);
                  const dstX2 = gx - 5 + xEdge, dstY2 = gy + dy;
                  const sx2   = Math.max(0, Math.round(dstX2 * dpr));
                  const sy2   = Math.round(dstY2 * dpr);
                  const sw2   = Math.round(wEdge * dpr);
                  const sh2   = Math.max(1, Math.round(dpr));
                  if (sy2 < 0 || sy2 + sh2 > gradCanvas.height || sx2 + sw2 > gradCanvas.width) continue;
                  sCtx.save();
                  sCtx.globalAlpha = Math.min(baseOp, 0.6);
                  sCtx.drawImage(gradCanvas, sx2, sy2, sw2, sh2, dstX2, dstY2, wEdge, 1);
                  sCtx.restore();
                }
                sCtx.fillStyle = 'rgba(0,0,0,0.12)';
                sCtx.fillRect(gx + Math.floor(Math.random() * SAMPLE_GRID), gy + Math.floor(Math.random() * SAMPLE_GRID), 1, 1);
                sCtx.fillStyle = 'rgba(255,255,255,0.07)';
                sCtx.fillRect(gx, gy, SAMPLE_GRID, 1);
              }

              let rColorStep = PASSES - 1;
              let rRow = totalRows - 1, rLtr = false, rHeadX = W, rLastX = W, rLastNow = 0;
              function reverseOutro(now) {
                if (stopped) return;
                const dt = rLastNow ? Math.min(now - rLastNow, 50) : 16;
                rLastNow = now;
                const rowY = rRow * SAMPLE_GRID;
                const adv  = SPEED * dt;
                if (rLtr) {
                  rHeadX = Math.min(W, rHeadX + adv);
                  while (rLastX + SAMPLE_GRID <= rHeadX) {
                    rLastX += SAMPLE_GRID;
                    if (rLastX >= 0 && rLastX < W) {
                      const gx = Math.floor(rLastX / SAMPLE_GRID) * SAMPLE_GRID;
                      const gy = Math.floor(rowY   / SAMPLE_GRID) * SAMPLE_GRID;
                      const key = `${gx},${gy}`;
                      if (punchAssignments.get(key) === rColorStep || blobAssignments.get(key) === rColorStep)
                        restoreCell(gx, gy);
                    }
                  }
                } else {
                  rHeadX = Math.max(0, rHeadX - adv);
                  while (rLastX - SAMPLE_GRID >= rHeadX) {
                    rLastX -= SAMPLE_GRID;
                    if (rLastX >= 0 && rLastX < W) {
                      const gx = Math.floor(rLastX / SAMPLE_GRID) * SAMPLE_GRID;
                      const gy = Math.floor(rowY   / SAMPLE_GRID) * SAMPLE_GRID;
                      const key = `${gx},${gy}`;
                      if (punchAssignments.get(key) === rColorStep || blobAssignments.get(key) === rColorStep)
                        restoreCell(gx, gy);
                    }
                  }
                }
                drawCursor(Math.round(rHeadX), rowY);
                const rowDone = rLtr ? rHeadX >= W : rHeadX <= 0;
                if (rowDone) {
                  if (rRow <= 0) {
                    rColorStep--;
                    if (rColorStep >= 0) {
                      rRow = totalRows - 1; rLtr = false; rHeadX = W; rLastX = W; rLastNow = 0;
                    } else {
                      clearCursor();
                      cursorCanvas.remove();
                      return;
                    }
                  } else {
                    rRow--; rLtr = !rLtr;
                    rHeadX = rLtr ? 0 : W;
                    rLastX = rLtr ? -SAMPLE_GRID : W;
                  }
                }
                raf = requestAnimationFrame(reverseOutro);
              }
              raf = requestAnimationFrame(reverseOutro);
            }

            raf = requestAnimationFrame(forwardOutro);
          }
          return;
        } else {
          advanceRow();
        }
      }
      raf = requestAnimationFrame(loop);
      return;
    }
  }

  holdTimer = setTimeout(() => {
    if (stopped) return;
    resetToColor(0);
    lastNow = 0;
    raf = requestAnimationFrame(loop);
  }, HOLD_MS);

  return {
    stop() {
      stopped = true;
      cancel();
      stitchCanvas.remove();
      cursorCanvas.remove();
      if (paintEl) paintEl.remove();
    }
  };
}

// ─── Simple CORS fallback ────────────────────────────────────────────────────
function simpleFade(gradCanvas, photoEl) {
  const start = performance.now();
  let raf = null;
  function tick(now) {
    const p = Math.min(1, (now - start) / 1200);
    gradCanvas.style.opacity = String(1 - p);
    if (p < 1) raf = requestAnimationFrame(tick);
  }
  raf = requestAnimationFrame(tick);
  return { stop() { if (raf) cancelAnimationFrame(raf); } };
}

// ─── Image loading + analysis ────────────────────────────────────────────────
function waitForImage(img) {
  return new Promise(resolve => {
    if (img.complete && img.naturalWidth > 0) resolve(img);
    else { img.onload = () => resolve(img); }
  });
}

function rgbToHsv(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r,g,b), min = Math.min(r,g,b);
  const v = max;
  const s = max === 0 ? 0 : (max-min)/max;
  let h = 0;
  if (max !== min) {
    const d = max-min;
    if (max===r) h=((g-b)/d+(g<b?6:0))/6;
    else if (max===g) h=((b-r)/d+2)/6;
    else h=((r-g)/d+4)/6;
  }
  return { h: h*360, s, v };
}

// Draw image exactly as object-fit:cover — crops to fill W×H, no stretch
function drawCover(ctx, img, W, H) {
  const nw = img.naturalWidth  || W;
  const nh = img.naturalHeight || H;
  const scale = Math.max(W / nw, H / nh);
  const sw = W / scale, sh = H / scale;
  const sx = (nw - sw) / 2, sy = (nh - sh) / 2;
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, W, H);
}

async function analysePhoto(imgEl, W, H, hexColors, passes) {
  const off = document.createElement('canvas');
  off.width = W; off.height = H;
  const ctx = off.getContext('2d', { willReadFrequently: true });
  try { drawCover(ctx, imgEl, W, H); }
  catch(e) { console.error('CORS:', e); return null; }

  const imgData = ctx.getImageData(0, 0, W, H).data;
  const N = passes || LOOM_PASSES;

  // ── Subject / object map ─────────────────────────────────────────────────────
  // Finer 16px grid: local contrast (luminance variance) + saturation + center weight
  const SAL_GRID  = 16;
  const salCols   = Math.ceil(W / SAL_GRID);
  const salRows   = Math.ceil(H / SAL_GRID);
  const salMap    = new Float32Array(salCols * salRows);
  for (let sy = 0; sy < salRows; sy++) {
    for (let sx = 0; sx < salCols; sx++) {
      let sumS = 0, sumLum = 0, sumLum2 = 0, count = 0;
      for (let py = sy * SAL_GRID; py < Math.min((sy + 1) * SAL_GRID, H); py += SAMPLE_GRID) {
        for (let px = sx * SAL_GRID; px < Math.min((sx + 1) * SAL_GRID, W); px += SAMPLE_GRID) {
          const i = (py * W + px) * 4;
          const { s, v } = rgbToHsv(imgData[i], imgData[i+1], imgData[i+2]);
          const lum = 0.299 * imgData[i] + 0.587 * imgData[i+1] + 0.114 * imgData[i+2];
          sumS += s; sumLum += lum; sumLum2 += lum * lum; count++;
        }
      }
      if (!count) continue;
      const avgS    = sumS / count;
      const avgLum  = sumLum / count;
      const variance = Math.sqrt(Math.max(0, sumLum2 / count - avgLum * avgLum)) / 255;
      const cx = (sx / salCols - 0.5) * 2;
      const cy = (sy / salRows - 0.5) * 2;
      const centerW = 1 - Math.sqrt(cx * cx + cy * cy) / Math.SQRT2;
      // Weighted: local contrast 40%, saturation 35%, center 25%
      salMap[sy * salCols + sx] = variance * 0.40 + avgS * 0.35 + centerW * 0.25;
    }
  }
  const N_TIERS = 6;
  function salTier(p) {
    const sx  = Math.min(Math.floor(p.x / SAL_GRID), salCols - 1);
    const sy  = Math.min(Math.floor(p.y / SAL_GRID), salRows - 1);
    const sal = salMap[sy * salCols + sx];
    // 0 = subject/foreground (high saliency), N_TIERS-1 = background
    return (N_TIERS - 1) - Math.min(N_TIERS - 1, Math.floor(sal * N_TIERS));
  }

  const palRGB = (hexColors && hexColors.length) ? hexColors.map(h => hexRgb(h)) : null;
  // Pre-convert palette to LAB for perceptually accurate deltaE matching
  const palLAB = palRGB ? palRGB.map(([r,g,b]) => rgbToLab(r, g, b)) : null;

  // 5×5 average of imgData at (x,y) — same area as sampleColor to reduce noise
  function avgPixel(x, y) {
    let r=0, g=0, b=0, count=0;
    for (let dy = -2; dy <= 2; dy++) {
      for (let dx = -2; dx <= 2; dx++) {
        const px = clamp(x + dx, 0, W - 1);
        const py = clamp(y + dy, 0, H - 1);
        const i  = (py * W + px) * 4;
        r += imgData[i]; g += imgData[i+1]; b += imgData[i+2]; count++;
      }
    }
    return [Math.round(r/count), Math.round(g/count), Math.round(b/count)];
  }

  // Find closest palette hex for a pixel at (x,y) using deltaE (LAB distance)
  function closestPal(x, y) {
    if (!palLAB) return null;
    const [r, g, b] = avgPixel(x, y);
    const lab = rgbToLab(r, g, b);
    let bestIdx = 0, bestDist = Infinity;
    palLAB.forEach((pLab, idx) => {
      const d = deltaE(lab, pLab);
      if (d < bestDist) { bestDist = d; bestIdx = idx; }
    });
    return { idx: bestIdx, dist: bestDist, rgb: palRGB[bestIdx] };
  }

  // All positions sorted together with the same key so punches and blobs
  // always reveal the same hue family at the same colorStep.
  const all = [];
  for (let y = 0; y < H; y += SAMPLE_GRID) {
    for (let x = 0; x < W; x += SAMPLE_GRID) {
      const [r, g, b] = avgPixel(x, y);
      const { h, s, v } = rgbToHsv(r, g, b);
      const cp = closestPal(x, y);
      // isPunch controls which positions clear through; all positions also get a blob
      all.push({ x, y, h, s, v, isPunch: Math.random() < 0.55, isBlob: true, palRgb: cp ? cp.rgb : null });
    }
  }

  // Compute average brightness (value) per hue family so we can order families
  // darkest → brightest. Hue family = floor(h / 30), giving 12 buckets.
  const famSumV  = new Float64Array(12);
  const famCount = new Int32Array(12);
  all.forEach(({ h, v }) => {
    const fam = Math.floor(h / 30) % 12;
    famSumV[fam]  += v;
    famCount[fam] += 1;
  });
  const famAvgV = new Float64Array(12);
  for (let f = 0; f < 12; f++) {
    famAvgV[f] = famCount[f] > 0 ? famSumV[f] / famCount[f] : 0;
  }
  // famRank[f] = 0 (darkest) → 11 (brightest)
  const famOrder = Array.from({ length: 12 }, (_, i) => i)
    .sort((a, b) => famAvgV[a] - famAvgV[b]);
  const famRank = new Int32Array(12);
  famOrder.forEach((fam, rank) => { famRank[fam] = rank; });

  // Shared sort: hue family ordered darkest→brightest, then saturation low→high
  all.sort((a, b) => {
    const rA = famRank[Math.floor(a.h / 30) % 12];
    const rB = famRank[Math.floor(b.h / 30) % 12];
    if (rA !== rB) return rA - rB;
    return a.s - b.s;
  });

  // Single pass over shared sorted array — all positions get blob assignments,
  // punch positions additionally get punch assignments (same step, same sequence)
  const perGroup = Math.ceil(all.length / N);
  const punchAssignments = new Map();
  const blobAssignments  = new Map();
  const blobColorMap     = new Map();
  all.forEach((p, i) => {
    const step = Math.min(Math.floor(i / perGroup), N - 1);
    // Every position gets a blob
    blobAssignments.set(`${p.x},${p.y}`, step);
    if (p.palRgb) blobColorMap.set(`${p.x},${p.y}`, p.palRgb);
    // 55% of positions also punch through
    if (p.isPunch) punchAssignments.set(`${p.x},${p.y}`, step);
  });

  return { punchAssignments, blobAssignments, blobColorMap };
}

// ─── Tooltip helpers ─────────────────────────────────────────────────────────
const TEMP_LABELS = { W: 'WARM', C: 'COOL', N: 'NEUTRAL' };

function showHstvTip(col, clientX, clientY) {
  const tip = document.getElementById('pal-hstv-tip');
  if (!tip) return;
  const mf = (window.SILKheeMetafields && window.SILKheeMetafields[col.code]) || {};
  const hex = mf.hex || col.hex || '#ccc';
  const isExclusive = EXCLUSIVE_CODES[String(col.code)] || false;

  const swatchEl    = document.getElementById('pht-swatch');
  const codeEl      = document.getElementById('pht-code');
  const nameEl      = document.getElementById('pht-name');
  const hexEl       = document.getElementById('pht-hex');
  const hvstEl      = document.getElementById('pht-hvst');
  const exclusiveEl = document.getElementById('pht-exclusive');

  if (swatchEl)    swatchEl.style.background = hex;
  if (codeEl)      codeEl.textContent  = col.code;
  if (nameEl)      nameEl.textContent  = (col.name || '').toUpperCase();
  if (hexEl)       hexEl.textContent   = String(hex).toUpperCase();
  if (exclusiveEl) exclusiveEl.style.display = isExclusive ? '' : 'none';

  if (hvstEl) {
    if (isExclusive) {
      hvstEl.textContent = 'EXCLUSIVE TO PACK';
    } else {
      const hue  = mf.hue  || '—';
      const val  = (mf.val  !== undefined && mf.val  !== null) ? mf.val  : '—';
      const sat  = mf.sat  || '—';
      const temp = mf.temp ? (TEMP_LABELS[mf.temp] || String(mf.temp).toUpperCase()) : '—';
      hvstEl.innerHTML =
        `<span class="cwt-hvst-item"><span class="cwt-hvst-lbl">HUE</span><span class="cwt-hvst-val">${hue}</span></span>` +
        `<span class="cwt-hvst-item"><span class="cwt-hvst-lbl">VAL</span><span class="cwt-hvst-val">${val}</span></span>` +
        `<span class="cwt-hvst-item"><span class="cwt-hvst-lbl">SAT</span><span class="cwt-hvst-val">${sat}</span></span>` +
        `<span class="cwt-hvst-item"><span class="cwt-hvst-lbl">TEMP</span><span class="cwt-hvst-val">${temp}</span></span>`;
    }
  }

  positionTip(tip, clientX, clientY);
  tip.setAttribute('aria-hidden', 'false');
  tip.style.opacity  = '1';
  tip.style.display  = '';
}

function positionTip(tip, clientX, clientY) {
  const OFFSET = 14;
  tip.style.left = '0';
  tip.style.top  = '0';
  const tw = tip.offsetWidth  || 200;
  const th = tip.offsetHeight || 120;
  let x = clientX + OFFSET;
  let y = clientY + OFFSET;
  if (x + tw > window.innerWidth  - 8) x = clientX - tw - OFFSET;
  if (y + th > window.innerHeight - 8) y = clientY - th - OFFSET;
  tip.style.left = x + 'px';
  tip.style.top  = y + 'px';
}

function hideHstvTip() {
  const tip = document.getElementById('pal-hstv-tip');
  if (!tip) return;
  tip.setAttribute('aria-hidden', 'true');
  tip.style.opacity = '0';
  tip.style.display = 'none';
}

// ─── Render colour cards ─────────────────────────────────────────────────────
function renderCards(container) {
  if (container.children.length) return;
  JSON.parse(container.dataset.colors || '[]').forEach(col => {
    const isExclusive = EXCLUSIVE_CODES[String(col.code)] || false;
    const el = document.createElement('div');
    el.className = 'pal-card';

    const nameHtml = isExclusive
      ? `<span class="pal-card-name pal-card-name--exclusive">EXCLUSIVE TO PACK</span>`
      : `<span class="pal-card-name">${col.name.toUpperCase()}</span>`;

    el.innerHTML = `<div class="pal-card-color" style="background:${col.hex}"></div>`
                 + `<div class="pal-card-label">`
                 + nameHtml
                 + `<span class="pal-card-code">${col.code}</span>`
                 + `</div>`;

    // Hover: HSTV tooltip
    el.addEventListener('mouseenter', e => { showHstvTip(col, e.clientX, e.clientY); });
    el.addEventListener('mousemove',  e => {
      const tip = document.getElementById('pal-hstv-tip');
      if (tip && tip.style.display !== 'none') positionTip(tip, e.clientX, e.clientY);
    });
    el.addEventListener('mouseleave', hideHstvTip);

    // Click: open modal (modal reads HSTV from window.SILKheeMetafields itself)
    el.addEventListener('click', e => {
      e.stopPropagation();
      hideHstvTip();
      if (typeof window.openColorModal !== 'function') return;
      const mf = (window.SILKheeMetafields && window.SILKheeMetafields[col.code]) || {};
      window.openColorModal({
        hex:  mf.hex || col.hex,
        name: col.name,
        code: String(col.code),
      });
    });

    container.appendChild(el);
  });
}

// ─── Close ───────────────────────────────────────────────────────────────────
function closeActive() {
  if (!activeRow) return;
  if (activeAnim) { activeAnim.stop(); activeAnim = null; }

  activeRow.querySelectorAll('.pal-anim-photo').forEach(el => el.remove());
  activeRow.querySelectorAll('.pal-anim-blob-canvas').forEach(el => el.remove());

  const gc = activeRow.querySelector('.pal-strip-canvas');
  if (gc) {
    gc.style.opacity = '1';
    gc.style.zIndex  = '2';
    const hex = JSON.parse(gc.dataset.colors || '[]');
    if (hex.length) renderStrip(gc, hex);
  }

  activeRow.querySelectorAll('img[data-anim-hidden]').forEach(img => {
    img.style.display = '';
    img.removeAttribute('data-anim-hidden');
  });

  const sw = activeRow.querySelector('.pal-strip-wrap');
  if (sw) sw.style.height = STRIP_H + 'px';

  activeRow.classList.remove('is-open');
  activeRow = null;
}

// ─── Open ─────────────────────────────────────────────────────────────────────
function openRow(row) {
  closeActive();

  const sw         = row.querySelector('.pal-strip-wrap');
  const gradCanvas = row.querySelector('.pal-strip-canvas');
  const listW      = (row.closest('.pal-list') || row).offsetWidth || 800;

  sw.style.height = EXPAND_H + 'px';
  row.classList.add('is-open');
  activeRow = row;

  const hexColors = JSON.parse(gradCanvas.dataset.colors || '[]');

  // Multi-photo: data-photo-cycle JSON array on .pal-strip-wrap
  let cycleUrls = null;
  try { if (sw.dataset.photoCycle) cycleUrls = JSON.parse(sw.dataset.photoCycle); } catch(e) {}

  // Single-photo fallback
  let photoUrl = null, origImg = null;
  row.querySelectorAll('.pal-strip-wrap img[src]').forEach(img => {
    if (!photoUrl && /\.(jpg|jpeg|png|webp|gif)(\?|$)/i.test(img.src)) {
      photoUrl = img.src;
      origImg  = img;
    }
  });

  if (!cycleUrls && !photoUrl) return;

  if (origImg) {
    origImg.style.display = 'none';
    origImg.setAttribute('data-anim-hidden', '1');
  }

  // z:1 — raw photo, always sharp (shows through punch holes crisp)
  const photoEl = document.createElement('img');
  photoEl.className   = 'pal-anim-photo';
  photoEl.crossOrigin = 'anonymous';
  photoEl.style.cssText = [
    'position:absolute', 'top:0', 'left:0',
    'width:100%', `height:${EXPAND_H}px`,
    'object-fit:cover', 'opacity:1',
    'z-index:1', 'pointer-events:none', 'display:block',
  ].join(';');
  sw.insertBefore(photoEl, sw.firstChild);

  // z:3 — gradient canvas (full opacity throughout)
  gradCanvas.style.zIndex   = '3';
  gradCanvas.style.opacity  = '1';
  gradCanvas.style.position = 'absolute';
  gradCanvas.style.top      = '0';
  gradCanvas.style.left     = '0';

  if (cycleUrls && cycleUrls.length > 1) {
    // ── Multi-image cycle ────────────────────────────────────────────────────
    // Each image runs the identical forward animation.
    // Image 1: gradient fill → loom → clear. Images 2+: no gradient fill → loom → clear.
    // Last image: no onDone — stays on revealed photo.
    let cycleIdx = 0;

    async function runCycle(prefillCanvas) {
      if (activeRow !== row || activeAnim) return;
      await waitForImage(photoEl);
      if (activeRow !== row) return;
      const result = await analysePhoto(photoEl, listW, EXPAND_H, hexColors);
      if (activeRow !== row) return;
      if (!result) { activeAnim = simpleFade(gradCanvas, photoEl); return; }

      const isLast = cycleIdx === cycleUrls.length - 1;
      const onDone = isLast ? null : () => {
        if (activeRow !== row) return;
        activeAnim = null;
        // Capture current revealed image onto an offscreen canvas.
        // It becomes the stitch-canvas cover for the next loom pass —
        // blobs paint over it, then punch through to reveal the next photo.
        const cap = document.createElement('canvas');
        cap.width  = listW;
        cap.height = EXPAND_H;
        drawCover(cap.getContext('2d'), photoEl, listW, EXPAND_H);
        cap.style.cssText = `position:absolute;top:0;left:0;width:100%;height:${EXPAND_H}px;z-index:4;pointer-events:none;`;
        sw.appendChild(cap);
        cycleIdx++;
        photoEl.src = cycleUrls[cycleIdx];
        runCycle(cap);
      };

      activeAnim = createRevealAnimation(
        gradCanvas, photoEl, listW, EXPAND_H,
        hexColors, result, null, onDone, false, prefillCanvas, true /* noBlobs */, isLast /* doReverse */
      );
    }

    photoEl.onload = () => runCycle(null);
    photoEl.src    = cycleUrls[0];
    if (photoEl.complete && photoEl.naturalWidth) runCycle(null);

  } else {
    // ── Single-image (Orchard) — final1withreverse animation ─────────────────
    async function startAnim() {
      if (activeRow !== row || activeAnim) return;
      await waitForImage(photoEl);
      if (activeRow !== row) return;
      const result = await analysePhoto(photoEl, listW, EXPAND_H, hexColors, ORCHARD_PASSES);
      if (activeRow !== row) return;
      if (!result) { activeAnim = simpleFade(gradCanvas, photoEl); return; }
      activeAnim = createOrchardAnimation(gradCanvas, photoEl, listW, EXPAND_H, hexColors, result, null);
    }

    photoEl.onload = startAnim;
    photoEl.src    = photoUrl || cycleUrls[0];
    if (photoEl.complete && photoEl.naturalWidth) startAnim();
  }
}

// ─── palToggle ────────────────────────────────────────────────────────────────
window.palToggle = function (sw, event) {
  if (event && event.target.closest('.pal-expanded')) return;
  const row = sw.closest('.pal-row');
  if (row === activeRow) { closeActive(); return; }
  openRow(row);
  const ce = row.querySelector('.pal-cards');
  if (ce && !ce.children.length) renderCards(ce);
};

// ─── Injected CSS ─────────────────────────────────────────────────────────────
function injectStyles() {
  const s = document.createElement('style');
  s.textContent = `
    .pal-strip-wrap {
      position: relative !important;
      overflow: hidden !important;
      height: ${STRIP_H}px !important;
      transition: height 500ms cubic-bezier(0.4,0,0.2,1) !important;
      cursor: pointer !important;
    }
    .pal-row.is-open .pal-strip-wrap {
      height: ${EXPAND_H}px !important;
      cursor: default !important;
    }
    .pal-strip-canvas {
      position: absolute !important;
      top: 0 !important; left: 0 !important;
      width: 100% !important;
      height: ${EXPAND_H}px !important;
      display: block !important;
      z-index: 2;
    }
  `;
  document.head.appendChild(s);
}

// ─── Init ─────────────────────────────────────────────────────────────────────
function renderAllStrips() {
  document.querySelectorAll('.pal-strip-canvas').forEach(canvas => {
    const hex = JSON.parse(canvas.dataset.colors || '[]');
    if (hex.length) renderStrip(canvas, hex);
  });
}

function init() {
  injectStyles();
  requestAnimationFrame(() => requestAnimationFrame(renderAllStrips));
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();

let _rt;
window.addEventListener('resize', () => { clearTimeout(_rt); _rt = setTimeout(renderAllStrips, 250); });

})();
